import React, { createContext, useContext, useState, useCallback } from "react";

const ToastContext = createContext({
  showToast: () => {},
  success: () => {},
  error: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

// Simple toast provider; uses in-memory list and auto-dismiss.
export default function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, { type = "info", duration = 3000, actionLabel, onAction } = {}) => {
    const canUseCrypto = typeof crypto !== "undefined" && crypto.randomUUID;
    const id = canUseCrypto ? crypto.randomUUID() : String(Date.now() + Math.random());
    setToasts((prev) => [
      ...prev,
      { id, message, type, actionLabel, onAction },
    ]);
    setTimeout(() => remove(id), duration);
    },
    [remove]
  );

  const success = useCallback((message) => showToast(message, { type: "success" }), [showToast]);
  const error = useCallback((message) => showToast(message, { type: "error" }), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error }}>
      {children}
      <div
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          zIndex: 2000,
        }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{
              padding: "10px 14px",
              borderRadius: "4px",
              background:
                toast.type === "success" ? "#d1fae5" : toast.type === "error" ? "#fee2e2" : "#e5e7eb",
              color: "#111827",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              minWidth: "220px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <span>{toast.message}</span>
            {toast.actionLabel && typeof toast.onAction === "function" && (
              <button
                type="button"
                onClick={() => {
                  toast.onAction?.();
                  remove(toast.id);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#1d4ed8",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                {toast.actionLabel}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
